import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isLoading, RenderModule } from 'holocron';
import { Button, Modal, ProgressCircular } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { IconChevronLeft } from '@americanexpress/dls-icons';
import { FormattedMessage } from 'react-intl';
import getIsXsTabOpen from '../../selectors/getXsTabOpen';
import themeContext from '../../context/themeContext';
import { setXsTabOpen } from '../../duck';
import styles from './XsLineOfBusinessModal.scss';
import getIsClearVisible from '../../selectors/getIsClearVisible';
import { TAB_TO_MODULE_NAME } from '../../constants/linesOfBusinessConstants';
import clearRequestThunk from '../../thunks/clearInputFieldsThink';

export const XsLineOfBusinessModal = ({ lineOfBusiness }) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsXsTabOpen);
  const isClearVisible = useSelector(getIsClearVisible);
  const theme = useContext(themeContext);
  const moduleName = TAB_TO_MODULE_NAME[lineOfBusiness];
  const isMounted = useRef(false);
  const isModuleLoading = useSelector(isLoading(moduleName));

  const closeModal = () => {
    dispatch(setXsTabOpen(false));
  };

  const clearInputFields = () => {
    dispatch(clearRequestThunk());
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      const tab = document.querySelector(`#${lineOfBusiness}-tab`);
      if (tab) {
        tab.focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  if (isModuleLoading) {
    return (
      <div className="text-align-center pad-3">
        <ProgressCircular size="lg" />
      </div>
    );
  }

  return (
    <Modal
      id={`${lineOfBusiness}-xs-tab-popup`}
      data-testid={`${lineOfBusiness}-xs-tab-popup`}
      className={clsx('height-full position-fixed-br position-fixed-tr', styles.popup)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${lineOfBusiness}-xs-tab-popup-title`}
    >
      <span className="sr-only">
        <FormattedMessage id={lineOfBusiness} />
      </span>
      <div
        id={`${lineOfBusiness}-xs-tab-popup-header`}
        className="dls-white-bg flex flex-justify-between border-b"
        style={{ padding: '4px' }}
      >
        <Button
          onClick={closeModal}
          role="button"
          variant="tertiary"
          id={`${lineOfBusiness}-xs-back-button`}
          data-testid={`${lineOfBusiness}-xs-back-button`}
          className="pad-1-l"
          onKeyDownCapture={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); closeModal(); }
          }}
          tabIndex={0}
          reactlytics={{ element: `${lineOfBusiness}-xs-back-button` }}
          icon={(<IconChevronLeft color="brand" size="md" />)}
        >
          <FormattedMessage id="back" />
        </Button>
        {isClearVisible && (
          <Button
            onClick={() => clearInputFields()}
            role="button"
            variant="tertiary"
            id={`${lineOfBusiness}-xs-clear-button`}
            data-testid={`${lineOfBusiness}-xs-clear-button`}
            onKeyDownCapture={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); }
            }}
            tabIndex={0}
            reactlytics={{ element: `${lineOfBusiness}-xs-clear-button` }}
          >
            <FormattedMessage id="clear" />
          </Button>
        )}
      </div>
      <div id={`${lineOfBusiness}-xs-body`} className="dls-gray-01-bg height-full">
        <RenderModule
          moduleName={moduleName}
          props={{ theme }}
        />
      </div>
    </Modal>
  );
};

XsLineOfBusinessModal.propTypes = {
  lineOfBusiness: PropTypes.string.isRequired,
};

export default XsLineOfBusinessModal;
